import { useEffect, useRef, useState } from "react";
import type { CurrentRound } from "../api";
import { serverNow } from "../utils/serverClock";

/**
 * How long after a round's draw time to ask for the next one.
 *
 * `/draws/current` keeps returning the expired round until the scheduler has
 * locked, drawn and settled it, so asking at the exact instant it closes gets
 * the round that just ended.
 */
const EXPIRY_GRACE_MS = 1500;

/** Gap between retries while the answer is still the round that just ended. */
const RETRY_MS = 3000;

const secondsUntil = (target: number) => Math.max(0, Math.ceil((target - serverNow()) / 1000));

/**
 * The two countdowns a round carries, derived from the round's own timestamps.
 *
 * Nothing here decrements a counter and nothing advances the period: the round
 * on screen is whatever the socket or `refresh` last delivered, and both clocks
 * are recomputed from its `closes_at` and `draw_at` every time they are read.
 * That is why the socket's `secondsRemaining` is deliberately ignored — two
 * writers rounding the same instant differently is what made the digits bounce.
 */
export const useDrawCountdown = (
  round: CurrentRound | null,
  refresh: () => Promise<unknown>
) => {
  const betsCloseAt = round?.closes_at;
  const drawAt = round?.draw_at;
  const [betSeconds, setBetSeconds] = useState(0);
  const [drawSeconds, setDrawSeconds] = useState(0);
  const refreshRef = useRef(refresh);

  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  useEffect(() => {
    const betsClose = Date.parse(betsCloseAt ?? "");
    const draw = Date.parse(drawAt ?? "");
    const tick = () => {
      setBetSeconds(Number.isFinite(betsClose) ? secondsUntil(betsClose) : 0);
      setDrawSeconds(Number.isFinite(draw) ? secondsUntil(draw) : 0);
    };
    tick();
    const interval = window.setInterval(tick, 250);
    // A hidden tab has its timers throttled, so the first thing to do on the
    // way back is read the clock rather than wait for the next tick.
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [betsCloseAt, drawAt]);

  useEffect(() => {
    const draw = Date.parse(drawAt ?? "");
    const hasExpired = () => Number.isFinite(draw) && serverNow() >= draw;
    let cancelled = false;
    let timer = 0;

    const schedule = (callback: () => void, delay: number) => {
      if (!cancelled) timer = window.setTimeout(callback, delay);
    };

    // Re-read the clock at least once a second rather than trusting one long
    // timeout: the server clock can be learned (or corrected) after this runs.
    const waitForDraw = () => {
      if (!Number.isFinite(draw)) return schedule(run, RETRY_MS);
      const left = draw + EXPIRY_GRACE_MS - serverNow();
      if (left <= 0) run();
      else schedule(waitForDraw, Math.min(left, 1_000));
    };

    const run = async () => {
      try {
        await refreshRef.current();
      } catch (error) {
        console.error("Draw refresh error:", error);
      }
      // A new round replaces `drawAt`, which re-runs this effect and clears
      // this timer. Reaching it means the answer was the same round again.
      schedule(waitForDraw, RETRY_MS);
    };

    waitForDraw();

    // Coming back to a throttled tab whose round is long gone: catch up on the
    // round that is open now rather than replaying the ones that were missed.
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible" || !hasExpired()) return;
      window.clearTimeout(timer);
      run();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [drawAt]);

  return { betSeconds, drawSeconds };
};
